import { Injectable } from '@angular/core';
import { getNewTooth, Tooth } from '@modules/teeth-diagram/models/tooth';
import { bridgeUnitType } from '@shared/models/consts';
import { IntuitiveBridge } from '../models/intuitive-bridge';

const MIN_TEETH_IN_BRIDGE_TO_CREATE = 1;
const MIN_TEETH_IN_BRIDGE_TO_SAVE = 2;

@Injectable({ providedIn: 'root' })
export class RxIntuitiveBridgeService {
	canCreateBridgeFromTeeth(selectedTeeth: Tooth[], jaws: { upperJaw: Tooth[]; lowerJaw: Tooth[] }): false | 'upper' | 'lower' {
		return this.canCreateBridgeFromTeethIds(
			selectedTeeth.map(t => t.ToothID).filter((id): id is number => id !== null),
			jaws
		);
	}

	canCreateBridgeFromTeethIds(
		selectedTeethIds: number[],
		{ upperJaw, lowerJaw }: { upperJaw: Tooth[]; lowerJaw: Tooth[] }
	): false | 'upper' | 'lower' {
		if (selectedTeethIds.length < MIN_TEETH_IN_BRIDGE_TO_CREATE) {
			return false;
		}

		let hasUpper = false;
		let hasLower = false;

		for (const tooth of selectedTeethIds) {
			hasUpper = hasUpper || upperJaw.some(j => j.ToothID === tooth);
			hasLower = hasLower || lowerJaw.some(j => j.ToothID === tooth);

			if (hasUpper && hasLower) {
				return false;
			}
		}

		return hasUpper ? 'upper' : 'lower';
	}

	createBridgeTeethSpan(
		selectedTeethIds: number[],
		{ upperJaw, lowerJaw }: { upperJaw: Tooth[]; lowerJaw: Tooth[] }
	): IntuitiveBridge | null {
		const bridgePosition = this.canCreateBridgeFromTeethIds(selectedTeethIds, { upperJaw, lowerJaw });

		if (bridgePosition === false) {
			return null;
		}

		let jaw: Tooth[];

		switch (bridgePosition) {
			case 'upper':
				jaw = upperJaw;
				break;
			case 'lower':
				jaw = lowerJaw;
				break;
			default:
				return null;
		}

		const borders = this.findBridgeBorders(selectedTeethIds, jaw);

		if (!borders) {
			return null;
		}

		const bridgeIndex = this.getNewBridgeIndex({ upperJaw, lowerJaw });
		const bridgeSpan = jaw
			.slice(borders.leftIndex, borders.rightIndex + 1)
			.filter(tooth => tooth.ToothID !== null)
			.map(tooth =>
				getNewTooth({
					ToothID: tooth.ToothID,
					UnitTypeID: bridgeUnitType,
					BridgeIndex: bridgeIndex
				})
			);

		return {
			index: bridgeIndex,
			outerLeftToothId: jaw[borders.leftIndex].ToothID,
			outerRightToothId: jaw[borders.rightIndex].ToothID,
			teeth: bridgeSpan,
			isSubmitted: false
		};
	}

	getBridgeSpanByBridgeIndex(bridgeIndex: number, flatTeeth: Tooth[]): IntuitiveBridge | null {
		const teethFound = flatTeeth.filter(t => t.BridgeIndex === bridgeIndex);

		if (teethFound.length === 0) {
			return null;
		}

		const borders = this.findBridgeBorders(
			teethFound.map(t => t.ToothID),
			flatTeeth
		);

		if (!borders) {
			return null;
		}

		return {
			index: bridgeIndex,
			outerLeftToothId: flatTeeth[borders.leftIndex].ToothID,
			outerRightToothId: flatTeeth[borders.rightIndex].ToothID,
			teeth: teethFound,
			isSubmitted: false
		};
	}

	getAllBridgesForJawIntuitive(jaw: Tooth[], selectedBridge?: IntuitiveBridge): Record<number, IntuitiveBridge> {
		const grouped = jaw.reduce((map, tooth) => this.groupTeethByBridgeIndex(map, tooth), new Map<number, Tooth[]>());
		const result: Record<number, IntuitiveBridge> = {};

		for (const [bridgeIndex, teeth] of grouped.entries()) {
			const borders = this.findBridgeBorders(
				teeth.map(t => t.ToothID),
				jaw
			);

			if (!borders) {
				continue;
			}

			result[bridgeIndex] = {
				index: bridgeIndex,
				outerLeftToothId: jaw[borders.leftIndex].ToothID,
				outerRightToothId: jaw[borders.rightIndex].ToothID,
				teeth,
				isSubmitted: false
			};
		}

		// Blend in the selected bridge to display latest changes without mutating the original jaw data
		if (selectedBridge) {
			result[selectedBridge.index] = selectedBridge;
		}

		return result;
	}

	canSaveBridgeTeeth(bridge: IntuitiveBridge): boolean {
		return bridge.teeth.length >= MIN_TEETH_IN_BRIDGE_TO_SAVE;
	}

	canRemoveToothFromBridge(bridge: IntuitiveBridge, selectedToothIds: number[]): boolean {
		if (!selectedToothIds.length) {
			return false;
		}

		if (bridge.teeth.length - selectedToothIds.length < MIN_TEETH_IN_BRIDGE_TO_CREATE) {
			return false;
		}

		const indices = selectedToothIds.map(id => bridge.teeth.findIndex(t => t.ToothID === id)).filter(i => i !== -1);

		if (indices.length !== selectedToothIds.length) {
			return false;
		}

		const minIndex = Math.min(...indices);
		const maxIndex = Math.max(...indices);

		const isContiguous = maxIndex - minIndex + 1 === indices.length;
		const touchesLeft = minIndex === 0;
		const touchesRight = maxIndex === bridge.teeth.length - 1;

		return isContiguous && touchesLeft !== touchesRight;
	}

	getTeethToExtendBridge(bridge: IntuitiveBridge, targetToothId: number, jaw: Tooth[]): Tooth[] | null {
		const bridgeToothId = bridge.outerLeftToothId ?? bridge.outerRightToothId;

		if (bridgeToothId == null) {
			return null;
		}

		const targetIndex = jaw.findIndex(t => t.ToothID === targetToothId);

		if (targetIndex === -1) {
			return null;
		}

		const bridgeToothIds = bridge.teeth.map(t => t.ToothID).filter((id): id is number => id !== null);
		const borders = this.findBridgeBorders(bridgeToothIds, jaw);

		if (!borders) {
			return null;
		}

		let teethSlice: Tooth[];

		if (targetIndex < borders.leftIndex) {
			teethSlice = jaw.slice(targetIndex, borders.leftIndex);
		} else if (targetIndex > borders.rightIndex) {
			teethSlice = jaw.slice(borders.rightIndex + 1, targetIndex + 1);
		} else {
			return null;
		}

		return teethSlice.filter(t => t.ToothID !== null);
	}

	buildExpandedBridge(existingBridge: IntuitiveBridge, additionalTeeth: Tooth[], jaw: Tooth[]): IntuitiveBridge {
		const mergedTeeth = [...existingBridge.teeth, ...additionalTeeth];
		const allToothIds = mergedTeeth.map(t => t.ToothID).filter((id): id is number => id !== null);
		const borders = this.findBridgeBorders(allToothIds, jaw);

		const orderedTeeth = jaw
			.filter((jt): jt is Tooth & { ToothID: number } => jt.ToothID !== null && allToothIds.includes(jt.ToothID))
			.map(jt => mergedTeeth.find(mt => mt.ToothID === jt.ToothID) as Tooth);

		return {
			...existingBridge,
			outerLeftToothId: borders ? jaw[borders.leftIndex].ToothID : existingBridge.outerLeftToothId,
			outerRightToothId: borders ? jaw[borders.rightIndex].ToothID : existingBridge.outerRightToothId,
			teeth: orderedTeeth
		};
	}

	hasUnsavedChanges(selectedBridge: IntuitiveBridge, persistedBridge: IntuitiveBridge): boolean {
		if (
			selectedBridge.outerLeftToothId !== persistedBridge.outerLeftToothId ||
			selectedBridge.outerRightToothId !== persistedBridge.outerRightToothId
		) {
			return true;
		}

		const selectedTeeth = selectedBridge.teeth.filter(t => t.ToothID !== null);

		if (selectedTeeth.length !== persistedBridge.teeth.length) {
			return true;
		}

		return this.hasTeethChanged({ ...selectedBridge, teeth: selectedTeeth }, persistedBridge);
	}

	private getNewBridgeIndex({ upperJaw, lowerJaw }: { upperJaw: Tooth[]; lowerJaw: Tooth[] }): number {
		const maxIndex = Math.max(...[...upperJaw, ...lowerJaw].map(tooth => tooth.BridgeIndex ?? 0));

		return maxIndex + 1;
	}

	private groupTeethByBridgeIndex(map: Map<number, Tooth[]>, tooth: Tooth): Map<number, Tooth[]> {
		if (tooth.ToothID == null || tooth.BridgeIndex === undefined || tooth.BridgeIndex === null) {
			return map;
		}

		const existing = map.get(tooth.BridgeIndex);

		if (existing) {
			existing.push(tooth);
		} else {
			map.set(tooth.BridgeIndex, [tooth]);
		}

		return map;
	}

	private hasTeethChanged(selectedBridge: IntuitiveBridge, persistedBridge: IntuitiveBridge): boolean {
		const persistedById = new Map(persistedBridge.teeth.map(t => [t.ToothID, t]));

		return selectedBridge.teeth.some(tooth => {
			const persisted = persistedById.get(tooth.ToothID);

			if (!persisted) {
				return true; // tooth not in persisted bridge — was added
			}

			return (Object.keys(tooth) as (keyof Tooth)[]).some(key => tooth[key] !== persisted[key]);
		});
	}

	private findBridgeBorders(teethIds: Tooth['ToothID'][], jaw: Tooth[]): { leftIndex: number; rightIndex: number } | undefined {
		const selectedIndices = teethIds.map(t => jaw.findIndex(jt => jt.ToothID === t)).filter(index => index !== -1);

		if (selectedIndices.length === 0) {
			return undefined;
		}

		return {
			leftIndex: Math.min(...selectedIndices),
			rightIndex: Math.max(...selectedIndices)
		};
	}
}
